import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Grid,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { Close, OpenInNew, ImageNotSupported, Restaurant, MenuBook, PictureAsPdf } from '@mui/icons-material';
import firebase from 'firebase/app';
import 'firebase/storage';
import { useRegionContext } from '../../region/RegionContext';

const MediaPreviewDialog = ({ open, onClose, business }) => {
  const { db } = useRegionContext();
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [menus, setMenus] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [menuFiles, setMenuFiles] = useState([]);
  const [menuFilesLoading, setMenuFilesLoading] = useState(false);

  const bizImages = business?.bizImages || business?.images || [];
  const documents = business?.bizDocuments || business?.documents || [];

  useEffect(() => {
    if (open && business?.id) {
      fetchMenusAndDishes(business.id);
      loadMenuFiles(business);
    }
  }, [open, business?.id]);

  const loadMenuFiles = async (biz) => {
    const paths = [
      ...(biz.pdfMenuStoragePath ? [{ path: biz.pdfMenuStoragePath, isPdf: true }] : []),
      ...(biz.menuImageStoragePaths || []).map(p => ({ path: p, isPdf: false })),
    ];
    if (!paths.length) return;
    setMenuFilesLoading(true);
    const resolved = await Promise.all(
      paths.map(async ({ path, isPdf }) => {
        try {
          const url = await firebase.storage().ref(path).getDownloadURL();
          return { url, isPdf };
        } catch { return null; }
      })
    );
    setMenuFiles(resolved.filter(Boolean));
    setMenuFilesLoading(false);
  };

  const fetchMenusAndDishes = async (bizId) => {
    try {
      setFetchLoading(true);
      const [menuSnap, dishSnap] = await Promise.all([
        db.collection('MenuDetails').where('menuBizId', '==', bizId).get(),
        db.collection('DishDetails').where('bizId', '==', bizId).get(),
      ]);

      setMenus(menuSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setDishes(dishSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      console.error('Error fetching menus/dishes:', err);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleClose = () => {
    setLightboxSrc(null);
    setActiveTab(0);
    setMenus([]);
    setDishes([]);
    setMenuFiles([]);
    setMenuFilesLoading(false);
    onClose();
  };

  const isPdf = (url) =>
    typeof url === 'string' &&
    (url.toLowerCase().includes('.pdf') || url.toLowerCase().includes('application%2Fpdf'));

  const dishImages = dishes.map(d => d.DishImage || d.dishImage).filter(Boolean);

  const sections = [
    { label: 'Business Images', count: bizImages.length },
    { label: 'Menu Files',      count: menuFiles.length },
    { label: 'Menus',           count: menus.length },
    { label: 'Dish Images',     count: dishImages.length },
    { label: 'Documents',       count: documents.length },
  ];

  const totalItems = bizImages.length + menuFiles.length + menus.length + dishImages.length + documents.length;

  const ImageGrid = ({ items }) => (
    <Grid container spacing={2}>
      {items.map((url, idx) => (
        <Grid item key={idx} xs={6} sm={4} md={3}>
          {isPdf(url) ? (
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                height: 140,
                justifyContent: 'center',
                bgcolor: 'grey.50',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                PDF Document {idx + 1}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                endIcon={<OpenInNew />}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open
              </Button>
            </Box>
          ) : (
            <Box
              component="img"
              src={url}
              alt={`Item ${idx + 1}`}
              onClick={() => setLightboxSrc(url)}
              sx={{
                width: '100%',
                height: 140,
                objectFit: 'cover',
                borderRadius: 1,
                cursor: 'pointer',
                border: 1,
                borderColor: 'divider',
                transition: 'opacity 0.15s',
                '&:hover': { opacity: 0.85 },
              }}
            />
          )}
        </Grid>
      ))}
    </Grid>
  );

  const EmptyState = ({ icon, message }) => (
    <Box sx={{ py: 5, textAlign: 'center' }}>
      {icon}
      <Typography color="text.secondary">{message}</Typography>
    </Box>
  );

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">{business?.bizName} — Media</Typography>
            <Typography variant="caption" color="text.secondary">
              {fetchLoading ? 'Loading menus & dishes...' : `${totalItems} item${totalItems !== 1 ? 's' : ''}`}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {sections.map((s, i) => (
              <Tab
                key={i}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {s.label}
                    {fetchLoading && (i === 2 || i === 3) ? (
                      <CircularProgress size={12} />
                    ) : (
                      <Chip label={s.count} size="small" />
                    )}
                  </Box>
                }
              />
            ))}
          </Tabs>

          {/* Business Images */}
          <Box hidden={activeTab !== 0} sx={{ p: 2 }}>
            {bizImages.length === 0 ? (
              <EmptyState
                icon={<ImageNotSupported sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />}
                message="No business images uploaded"
              />
            ) : (
              <ImageGrid items={bizImages} />
            )}
          </Box>

          {/* Menu Files */}
          <Box hidden={activeTab !== 1} sx={{ p: 2 }}>
            {menuFilesLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={32} /></Box>
            ) : menuFiles.length === 0 ? (
              <EmptyState
                icon={<PictureAsPdf sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />}
                message="No scraped menu files — use 'Scrape Menu' on the venue row to fetch from the website"
              />
            ) : (
              <Box>
                {menuFiles.filter(f => f.isPdf).map((f, i) => (
                  <Box key={i} sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      PDF Menu
                    </Typography>
                    <Box component="embed" src={f.url} type="application/pdf"
                         sx={{ width: '100%', height: 480, borderRadius: 1,
                               border: 1, borderColor: 'divider' }} />
                  </Box>
                ))}
                {menuFiles.filter(f => !f.isPdf).length > 0 && (
                  <Box sx={{ mt: menuFiles.some(f => f.isPdf) ? 2 : 0 }}>
                    {menuFiles.some(f => f.isPdf) && (
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Menu Images</Typography>
                    )}
                    <ImageGrid items={menuFiles.filter(f => !f.isPdf).map(f => f.url)} />
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Menus */}
          <Box hidden={activeTab !== 2} sx={{ p: 2 }}>
            {fetchLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={32} /></Box>
            ) : menus.length === 0 ? (
              <EmptyState
                icon={<MenuBook sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />}
                message="No menus created for this venue"
              />
            ) : (
              <List disablePadding>
                {menus.map((menu, idx) => (
                  <React.Fragment key={menu.id}>
                    {idx > 0 && <Divider />}
                    <ListItem sx={{ px: 1 }}>
                      <ListItemText
                        primary={menu.menuName || 'Unnamed Menu'}
                        secondary={
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                            {menu.menuDesc && (
                              <Typography variant="caption" color="text.secondary">
                                {menu.menuDesc}
                              </Typography>
                            )}
                            {menu.menuType && <Chip label={menu.menuType} size="small" variant="outlined" />}
                            {menu.menuOn !== undefined && (
                              <Chip
                                label={menu.menuOn ? 'Active' : 'Inactive'}
                                size="small"
                                color={menu.menuOn ? 'success' : 'default'}
                              />
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>

          {/* Dish Images */}
          <Box hidden={activeTab !== 3} sx={{ p: 2 }}>
            {fetchLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={32} /></Box>
            ) : dishImages.length === 0 ? (
              <EmptyState
                icon={<Restaurant sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />}
                message="No dish images uploaded for this venue"
              />
            ) : (
              <ImageGrid items={dishImages} />
            )}
          </Box>

          {/* Documents */}
          <Box hidden={activeTab !== 4} sx={{ p: 2 }}>
            {documents.length === 0 ? (
              <EmptyState
                icon={<ImageNotSupported sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />}
                message="No verification documents uploaded"
              />
            ) : (
              <ImageGrid items={documents} />
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Lightbox */}
      <Dialog open={!!lightboxSrc} onClose={() => setLightboxSrc(null)} maxWidth="lg">
        <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'black' }}>
          <IconButton
            onClick={() => setLightboxSrc(null)}
            sx={{ position: 'absolute', top: 8, right: 8, color: 'white', zIndex: 1 }}
          >
            <Close />
          </IconButton>
          {lightboxSrc && (
            <Box
              component="img"
              src={lightboxSrc}
              alt="Preview"
              sx={{ maxWidth: '90vw', maxHeight: '85vh', display: 'block' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MediaPreviewDialog;
